import { useRef } from 'react';
import { toast } from 'sonner';
import {
  CopyIcon,
  MailIcon,
  MessageSquareIcon,
  Share2Icon,
  XIcon,
} from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { copyText } from '@/lib/utils';
import type { QuoteShareLinkDto } from '@/lib/api/quotes';

import { formatDateTime } from './helpers';

interface Props {
  link: QuoteShareLinkDto;
  /** Job customer's contact, when resolvable — prefilled as the mail/SMS recipient. */
  recipientEmail?: string | null;
  recipientPhone?: string | null;
  onClose: () => void;
}

const canNativeShare = () =>
  typeof navigator !== 'undefined' && typeof navigator.share === 'function';

// Shown after `POST .../share-link` succeeds. The backend returns the URL only;
// we compose the delivery here — copy, native share sheet, and prefilled
// mailto: / sms:. The token is capability-bearing, so it's never logged.
export function ShareLinkPanel({
  link,
  recipientEmail,
  recipientPhone,
  onClose,
}: Props) {
  const { t } = useLanguage();
  const inputRef = useRef<HTMLInputElement>(null);

  const body = `${t.quoteShareEmailBody}\n\n${link.url}`;
  const mailtoHref = `mailto:${recipientEmail ?? ''}?subject=${encodeURIComponent(
    t.quoteShareEmailSubject,
  )}&body=${encodeURIComponent(body)}`;
  const smsHref = `sms:${recipientPhone ?? ''}?&body=${encodeURIComponent(body)}`;

  async function handleCopy() {
    const ok = await copyText(link.url);
    if (ok) {
      toast.success(t.quoteLinkCopied);
    } else {
      inputRef.current?.select();
      toast.error(t.quoteLinkCopyFailed);
    }
  }

  async function handleNativeShare() {
    try {
      await navigator.share({
        title: t.quoteShareEmailSubject,
        text: t.quoteShareEmailBody,
        url: link.url,
      });
    } catch (err) {
      // User dismissed the share sheet — not an error.
      if (err instanceof Error && err.name === 'AbortError') return;
      const ok = await copyText(link.url);
      if (ok) toast.success(t.quoteLinkCopied);
    }
  }

  return (
    <div
      className="flex flex-col gap-3 border-t pt-4"
      data-testid="quote-share-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">{t.quoteShareLinkPanelTitle}</p>
          <p className="text-xs text-muted-foreground">{t.quoteShareLinkHint}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t.quoteShareClose}
          className="text-muted-foreground hover:text-foreground transition-colors rounded-md p-1"
          data-testid="quote-share-panel-close"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          readOnly
          value={link.url}
          onFocus={(e) => e.currentTarget.select()}
          data-testid="quote-share-url"
          className="flex-1 min-w-0 px-3 py-2 text-sm border rounded-md bg-muted/40 focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <Button
          type="button"
          size="sm"
          onClick={handleCopy}
          data-testid="quote-share-copy"
        >
          <CopyIcon className="w-3.5 h-3.5" />
          {t.quoteShareCopy}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {canNativeShare() && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleNativeShare}
            data-testid="quote-share-native"
          >
            <Share2Icon className="w-3.5 h-3.5" />
            {t.quoteShareNative}
          </Button>
        )}
        <Button asChild size="sm" variant="outline" data-testid="quote-share-email">
          <a href={mailtoHref}>
            <MailIcon className="w-3.5 h-3.5" />
            {t.quoteShareEmail}
          </a>
        </Button>
        <Button asChild size="sm" variant="outline" data-testid="quote-share-text">
          <a href={smsHref}>
            <MessageSquareIcon className="w-3.5 h-3.5" />
            {t.quoteShareText}
          </a>
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        {t.quoteShareExpires} {formatDateTime(link.expires_at)}
      </p>
    </div>
  );
}
